const normalizeSearchTerm = (term) => {
    return term.replace(/^0+/, '').toLowerCase();
}

const expandAbbreviations = (term) => {
    const abbreviations = {
        'st': ['street', 'saint', 'st'],
        'rd': ['road', 'rd'],
        'ave': ['avenue', 'ave'],
        'dr': ['drive', 'dr'],
        'ln': ['lane', 'ln'],
        'ct': ['court', 'ct'],
        'pl': ['place', 'pl'],
        'blvd': ['boulevard', 'blvd'],
        'pkwy': ['parkway', 'pkwy'],
        'sq': ['square', 'sq'],
    };

    const lower = term.toLowerCase();
    if (abbreviations[lower]) {
        return abbreviations[lower];
    }
    return [term];
}

const fuzzyMatch = (searchTerm, text) => {
    const normalizedSearch = normalizeSearchTerm(searchTerm);
    const normalizedText = text.toLowerCase();

    if (normalizedText.includes(normalizedSearch)) {
        return { matched: true, exact: true, position: normalizedText.indexOf(normalizedSearch) };
    }

    const expanded = expandAbbreviations(normalizedSearch);
    for (const variant of expanded) {
        if (normalizedText.includes(variant)) {
            return { matched: true, exact: false, position: normalizedText.indexOf(variant) };
        }
    }

    if (normalizedSearch.length >= 3) {
        for (let i = 0; i <= normalizedText.length - normalizedSearch.length; i++) {
            const substring = normalizedText.substring(i, i + normalizedSearch.length);
            let differences = 0;
            for (let j = 0; j < normalizedSearch.length; j++) {
                if (substring[j] !== normalizedSearch[j]) {
                    differences++;
                }
            }
            if (differences <= 1 && normalizedSearch.length >= 3) {
                return { matched: true, exact: false, position: i, fuzzy: true };
            }
        }
    }

    return { matched: false };
}

const calculateRelevanceScore = (searchTerms, values) => {
    if (!searchTerms.length) {
        return 0;
    }

    const normalizedValues = values.toLowerCase();
    const normalizedSearch = searchTerms.join(' ').toLowerCase();

    let exactPhraseScore = 0;
    if (normalizedValues.includes(normalizedSearch)) {
        exactPhraseScore = 1000;
        const position = normalizedValues.indexOf(normalizedSearch);
        exactPhraseScore += (100 - Math.min(position, 100));
    }

    const wordMatches = [];
    const wordPositions = [];

    for (const term of searchTerms) {
        const match = fuzzyMatch(term, values);
        if (match.matched) {
            wordMatches.push(match);
            wordPositions.push(match.position);
        }
    }

    if (wordMatches.length === 0) {
        return 0;
    }

    const allWordsMatch = wordMatches.length === searchTerms.length;
    const exactMatches = wordMatches.filter(m => m.exact && !m.fuzzy).length;
    const fuzzyMatches = wordMatches.filter(m => m.fuzzy).length;

    let score = 0;

    if (exactPhraseScore > 0) {
        score = exactPhraseScore;
    } else if (allWordsMatch) {
        score = 500;
        score += exactMatches * 50;
        score -= fuzzyMatches * 20;

        if (wordPositions.length > 1) {
            const sortedPositions = [...wordPositions].sort((a, b) => a - b);
            let proximityBonus = 0;
            for (let i = 1; i < sortedPositions.length; i++) {
                const distance = sortedPositions[i] - sortedPositions[i - 1];
                if (distance < 20) {
                    proximityBonus += (20 - distance);
                }
            }
            score += Math.min(proximityBonus, 100);

            const isInOrder = wordPositions.every((pos, i) => {
                if (i === 0) return true;
                return pos >= wordPositions[i - 1];
            });
            if (isInOrder) {
                score += 50;
            }
        }
    } else {
        const matchRatio = wordMatches.length / searchTerms.length;
        score = 100 * matchRatio;
        score += exactMatches * 30;
        score -= fuzzyMatches * 10;
    }

    const earlyMatchBonus = Math.max(0, 50 - Math.min(...wordPositions));
    score += earlyMatchBonus;

    return score;
}

const liveSearch = (search) => {
    return (items) => {
        if (!search) {
            return items
        }

        if (items === null) {
            return null
        }
        if (Array.isArray(items)) {
            return items.filter(item => liveSearch(search)(item))
        }

        if (typeof items !== 'object') {
            return false
        }

        const values = getValueString(items).toLowerCase();
        const searchTerms = search.toLowerCase().split(/\W+/).filter(s => s.length > 0).map(normalizeSearchTerm);

        if (searchTerms.length === 0) {
            return false;
        }

        const score = calculateRelevanceScore(searchTerms, values);
        return score > 0;
    }
}

const liveSearchWithScore = (search) => {
    return (items) => {
        if (!search) {
            return { match: true, score: 0 };
        }

        if (items === null) {
            return { match: false, score: 0 };
        }
        if (Array.isArray(items)) {
            return items.map(item => liveSearchWithScore(search)(item));
        }

        if (typeof items !== 'object') {
            return { match: false, score: 0 };
        }

        const values = getValueString(items).toLowerCase();
        const searchTerms = search.toLowerCase().split(/\W+/).filter(s => s.length > 0).map(normalizeSearchTerm);

        if (searchTerms.length === 0) {
            return { match: false, score: 0 };
        }

        const score = calculateRelevanceScore(searchTerms, values);
        return { match: score > 0, score };
    }
}

const rankedSearch = (search) => {
    return (items) => {
        if (!search || !Array.isArray(items)) {
            return items;
        }

        const scored = items.map(item => ({
            item,
            ...liveSearchWithScore(search)(item)
        }));

        return scored
            .filter(result => result.match)
            .sort((a, b) => b.score - a.score)
            .map(result => result.item);
    }
}

const getValueString = (obj, str = '') => {
    if (obj === null || obj === undefined) {
        return str;

    } else if (typeof obj === 'object') {
        return Object.values(obj).reduce((str, v) => getValueString(v, str), str);

    } else if (Array.isArray(obj)) {
        return obj.reduce((str, v) => {
            return getValueString(v, str);
        }, str);

    } else if (typeof obj === 'string') {
        return str + ' ' + obj;

    } else {
        return str;
    }
}

export {
    liveSearch,
    liveSearchWithScore,
    rankedSearch,
}
